/**
 * SectionTeam — las personas que trabajarán en la propiedad.
 *
 * Es la maqueta del futuro modelo de equipo por propiedad: aquí se ve quién
 * hace qué y sobre qué alojamientos, no cómo se entra hoy en la plataforma.
 * Nadie de esta lista existe como usuario real y ninguna acción de esta
 * pantalla envía nada a nadie.
 *
 * Tres decisiones que no son obvias:
 * 1. La invitación solo añade una fila con estado «Invitación enviada». Se
 *    llama así porque es lo que pasará el día que el modelo sea real; en la
 *    vista previa no sale ningún correo. La nota de arriba lo dice sin rodeos
 *    para que nadie espere un mensaje que no va a llegar.
 * 2. Cada persona tiene un rol y un ámbito. El rol dice qué puede hacer y se
 *    describe en la sección Roles; el ámbito dice sobre qué alojamientos, y es
 *    el campo que permite que una limpiadora vea solo su bloque de villas.
 * 3. Una sola fila para móvil y escritorio, con clases responsivas. Duplicar
 *    el componente sería la vía rápida, pero acaba en dos listas que se
 *    desincronizan.
 */
import React, { useState } from "react";
import { Plus, PencilSimple, Envelope, DeviceMobile } from "@phosphor-icons/react";
import Chip from "@/components/ui-dv/Chip";
import SidePanel from "@/components/ui-dv/SidePanel";
import {
  Block, Card, FieldGrid, Field, Select, Note,
} from "@/pages/admin/pms/settings/SettingsPrimitives";
import { useSettingsV2, addTeamMember, updateTeamMember } from "@/lib/settingsV2Store";
import { TEAM_STATUS_LABEL, ROLE_TEMPLATES } from "@/data/settingsV2Demo";

const ROLE_OPTIONS = ROLE_TEMPLATES.map((r) => ({ value: r.role_key, label: r.label }));
const ROLE_LABEL = Object.fromEntries(ROLE_TEMPLATES.map((r) => [r.role_key, r.label]));
const ROLE_SUMMARY = Object.fromEntries(ROLE_TEMPLATES.map((r) => [r.role_key, r.summary]));

const STATUS_OPTIONS = Object.entries(TEAM_STATUS_LABEL).map(([value, label]) => ({ value, label }));

/** Verde para quien ya trabaja, ámbar para quien está pendiente, gris para quien no. */
const STATUS_TONE = { active: "emerald", invited: "amber", inactive: "stone" };

const EMPTY_INVITE = { name: "", email: "", phone: "", role: "reception", scope: "" };

/** Iniciales del nombre: identifica la fila de un vistazo sin pedir fotos. */
function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean).slice(0, 2);
  if (parts.length === 0) return "—";
  return parts.map((w) => w[0]).join("").toUpperCase();
}

export default function SectionTeam() {
  const { team, property } = useSettingsV2();
  const [invite, setInvite] = useState(null);
  const [editing, setEditing] = useState(null);

  const activeCount = team.filter((m) => m.status === "active").length;
  const invitedCount = team.filter((m) => m.status === "invited").length;

  const sendInvite = () => {
    if (!invite) return;
    const name = invite.name.trim();
    if (!name) return;
    const scope = invite.scope.trim();
    addTeamMember({
      name,
      email: invite.email.trim(),
      phone: invite.phone.trim(),
      role: invite.role,
      status: "invited",
      ...(scope ? { scope } : {}),
    });
    setInvite(null);
  };

  const saveEdit = () => {
    if (!editing) return;
    updateTeamMember(editing.member_id, {
      name: editing.name.trim(),
      role: editing.role,
      scope: editing.scope.trim(),
      status: editing.status,
    });
    setEditing(null);
  };

  return (
    <div data-testid="section-team">
      <Block
        title="Equipo"
        description="Quién trabaja en la propiedad, con qué rol y sobre qué alojamientos."
        testId="team-block"
        action={
          <button
            type="button"
            onClick={() => setInvite({ ...EMPTY_INVITE })}
            data-testid="team-invite"
            className="dv-btn dv-btn-outline text-xs min-h-[44px] inline-flex items-center gap-1.5"
          >
            <Plus size={14} weight="bold" />
            Invitar miembro
          </button>
        }
      >
        <Note testId="team-mockup-note">
          Esta lista es una maqueta del futuro equipo por propiedad. No crea usuarios, no envía
          invitaciones y no cambia las cuentas reales de la plataforma: las personas que ves son
          ficticias y todo lo que hagas aquí se queda en la vista previa.
        </Note>

        <Card padded={false} testId="team-list">
          <ul className="divide-y divide-stone-100 dark:divide-stone-800">
            {team.map((m) => (
              <li key={m.member_id}>
                <button
                  type="button"
                  onClick={() => setEditing({ ...m })}
                  data-testid={`team-row-${m.member_id}`}
                  className="w-full text-left px-4 py-3 min-h-[56px] hover:bg-stone-50 dark:hover:bg-stone-900 transition-colors"
                >
                  <span className="flex items-start sm:items-center gap-3">
                    <span className="shrink-0 w-9 h-9 rounded-full bg-stone-100 dark:bg-stone-800 flex items-center justify-center text-[11px] tracking-wide text-stone-600 dark:text-stone-300">
                      {initials(m.name)}
                    </span>

                    <span className="min-w-0 flex-1 sm:flex sm:items-center sm:gap-3">
                      {/* Nombre y rol */}
                      <span className="min-w-0 sm:flex-1">
                        <span className="block text-sm text-stone-900 dark:text-stone-100 truncate">
                          {m.name || "Sin nombre"}
                        </span>
                        <span className="block text-xs text-stone-500 dark:text-stone-400 truncate">
                          {ROLE_LABEL[m.role] || m.role}
                        </span>
                      </span>

                      {/* Ámbito */}
                      <span className="block text-xs text-stone-500 dark:text-stone-400 truncate mt-0.5 sm:mt-0 sm:w-40 sm:shrink-0">
                        {m.scope || property.name}
                      </span>

                      {/* Contacto */}
                      <span className="block text-xs text-stone-400 dark:text-stone-500 mt-0.5 sm:mt-0 sm:w-44 sm:shrink-0">
                        <span className="flex items-center gap-1 truncate">
                          <Envelope size={12} className="shrink-0" />
                          <span className="truncate">{m.email || "—"}</span>
                        </span>
                        <span className="flex items-center gap-1 truncate">
                          <DeviceMobile size={12} className="shrink-0" />
                          <span className="truncate">{m.phone || "—"}</span>
                        </span>
                      </span>

                      {/* Estado */}
                      <span className="flex items-center gap-2 mt-2 sm:mt-0 sm:shrink-0">
                        <Chip tone={STATUS_TONE[m.status] || "stone"} size="xs" dot>
                          {TEAM_STATUS_LABEL[m.status] || m.status}
                        </Chip>
                        <PencilSimple size={14} className="text-stone-400 shrink-0" />
                      </span>
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <p className="mt-3 text-xs text-stone-500 dark:text-stone-400" data-testid="team-summary">
          {team.length} personas · {activeCount} en activo · {invitedCount} pendientes de aceptar.
          Lo que puede ver y hacer cada rol se explica en la sección Roles.
        </p>
      </Block>

      {/* --------------------------------------------------------------- */}
      {/* Invitar */}
      {/* --------------------------------------------------------------- */}
      <SidePanel
        open={!!invite}
        onClose={() => setInvite(null)}
        title="Invitar a alguien al equipo"
        subtitle="Equipo"
        testId="team-invite-panel"
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setInvite(null)}
              data-testid="team-invite-cancel"
              className="dv-btn dv-btn-ghost text-sm min-h-[44px]"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={sendInvite}
              disabled={!invite?.name.trim()}
              data-testid="team-invite-submit"
              className={`dv-btn dv-btn-primary text-sm min-h-[44px] ${
                invite?.name.trim() ? "" : "opacity-50 cursor-not-allowed"
              }`}
            >
              Enviar invitación
            </button>
          </div>
        }
      >
        {invite && (
          <div className="space-y-5">
            <p className="text-xs text-stone-500">
              En la vista previa esto solo añade una fila con estado «Invitación enviada».
              No se manda ningún correo ni se crea ninguna cuenta.
            </p>

            <FieldGrid cols={2}>
              <Field
                label="Nombre"
                value={invite.name}
                onChange={(v) => setInvite({ ...invite, name: v })}
                placeholder="Nombre y apellido"
                span={2}
                testId="team-invite-name"
              />
              <Field
                label="Correo"
                type="email"
                value={invite.email}
                onChange={(v) => setInvite({ ...invite, email: v })}
                placeholder="nombre@ejemplo.demo"
                testId="team-invite-email"
              />
              <Field
                label="Teléfono"
                value={invite.phone}
                onChange={(v) => setInvite({ ...invite, phone: v })}
                placeholder="+62 811 000 000"
                testId="team-invite-phone"
              />
              <Select
                label="Rol"
                value={invite.role}
                onChange={(v) => setInvite({ ...invite, role: v })}
                options={ROLE_OPTIONS}
                hint="Define qué ve esta persona"
                span={2}
                testId="team-invite-role"
              />
              <Field
                label="Ámbito"
                value={invite.scope}
                onChange={(v) => setInvite({ ...invite, scope: v })}
                placeholder={property.name}
                hint="Toda la propiedad o un grupo de alojamientos"
                span={2}
                testId="team-invite-scope"
              />
            </FieldGrid>

            <p className="text-xs text-stone-500">{ROLE_SUMMARY[invite.role]}</p>
          </div>
        )}
      </SidePanel>

      {/* --------------------------------------------------------------- */}
      {/* Editar */}
      {/* --------------------------------------------------------------- */}
      <SidePanel
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.name || "Miembro del equipo"}
        subtitle="Equipo"
        testId="team-edit-panel"
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(null)}
              data-testid="team-edit-cancel"
              className="dv-btn dv-btn-ghost text-sm min-h-[44px]"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={saveEdit}
              data-testid="team-edit-save"
              className="dv-btn dv-btn-primary text-sm min-h-[44px]"
            >
              Guardar
            </button>
          </div>
        }
      >
        {editing && (
          <div className="space-y-5">
            <FieldGrid cols={2}>
              <Field
                label="Nombre"
                value={editing.name}
                onChange={(v) => setEditing({ ...editing, name: v })}
                span={2}
                testId="team-edit-name"
              />
              <Select
                label="Rol"
                value={editing.role}
                onChange={(v) => setEditing({ ...editing, role: v })}
                options={ROLE_OPTIONS}
                hint="Se describe en Roles"
                testId="team-edit-role"
              />
              <Select
                label="Estado"
                value={editing.status}
                onChange={(v) => setEditing({ ...editing, status: v })}
                options={STATUS_OPTIONS}
                testId="team-edit-status"
              />
              <Field
                label="Ámbito"
                value={editing.scope}
                onChange={(v) => setEditing({ ...editing, scope: v })}
                placeholder={property.name}
                hint="Toda la propiedad o un grupo de alojamientos"
                span={2}
                testId="team-edit-scope"
              />
            </FieldGrid>

            <p className="text-xs text-stone-500">{ROLE_SUMMARY[editing.role]}</p>

            <div className="pt-4 border-t border-stone-100 dark:border-stone-800 space-y-1 text-xs text-stone-500">
              <div className="flex items-center gap-2">
                <Envelope size={12} className="shrink-0" />
                <span className="truncate">{editing.email || "Sin correo"}</span>
              </div>
              <div className="flex items-center gap-2">
                <DeviceMobile size={12} className="shrink-0" />
                <span className="truncate">{editing.phone || "Sin teléfono"}</span>
              </div>
              <p className="pt-2">
                El correo y el teléfono se muestran tal como llegaron en la invitación. En la
                vista previa no se pueden cambiar ni se usan para avisar a nadie.
              </p>
            </div>
          </div>
        )}
      </SidePanel>
    </div>
  );
}
